import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { User } from "@/api/entities";
import {
  subscribePush,
  localTestNotification,
  ensureSW,
  pushSupportReason,
} from "@/lib/push";

const HAS_VAPID = !!import.meta.env.VITE_VAPID_PUBLIC_KEY;

function PushSetupPanel({ scheduleTime: _scheduleTime }) {
  const location = useLocation();

  // ✅ Mostra box rosso + link SOLO in dashboard (non in setup)
  const showDashboardHint = location?.pathname?.includes("/dashboard");

  const [state, setState] = useState({
    perm:
      (typeof Notification !== "undefined" && Notification.permission) ||
      "default",
    hasSub: false,
    swOk: false,
    reason: "checking", // ok | ios-need-install | no-sw | no-notifications | no-push | checking
    lastErr: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const reason = pushSupportReason();
        if (reason !== "ok") {
          if (!cancelled)
            setState((s) => ({ ...s, reason, swOk: false, lastErr: "" }));
          return;
        }
        await ensureSW();
        const ready = await navigator.serviceWorker.ready;
        const swOk = !!ready && !!ready.active;

        let hasSub = false;
        try {
          hasSub = !!(await ready.pushManager.getSubscription());
        } catch {}

        if (!cancelled) {
          setState({
            perm: Notification.permission,
            hasSub,
            swOk,
            reason: "ok",
            lastErr: "",
          });
        }
      } catch (e) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            swOk: false,
            reason: "no-sw",
            lastErr: String(e?.message || e),
          }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onEnablePush() {
    try {
      if (!HAS_VAPID) throw new Error("VITE_VAPID_PUBLIC_KEY mancante.");
      if (state.reason !== "ok") {
        if (state.reason === "ios-need-install")
          throw new Error("Su iPhone installa l’app nella Home, poi riaprila.");
        throw new Error("Questo dispositivo/browser non supporta Web Push.");
      }
      if (!state.swOk) {
        await ensureSW();
        await navigator.serviceWorker.ready;
      }

      let perm = Notification.permission;
      if (perm !== "granted") {
        perm = await Notification.requestPermission();
        if (perm !== "granted") {
          throw new Error(
            perm === "denied"
              ? "Permesso negato: abilitalo dalle impostazioni del sito."
              : "Permesso non concesso."
          );
        }
      }

      // ✅ micro-fix: evita userId "anon" quando l’utente è loggato
      let me = null;
      try {
        me = await User.me();
      } catch {}

      await subscribePush({ userId: me?.id });

      const ready = await navigator.serviceWorker.ready;
      const sub = await ready.pushManager.getSubscription();

      setState({
        perm: "granted",
        hasSub: !!sub,
        swOk: true,
        reason: "ok",
        lastErr: "",
      });

      alert("Notifiche attivate ✅");
    } catch (e) {
      const msg = e?.message || String(e);
      setState((s) => ({ ...s, lastErr: msg }));
      alert("Attivazione non riuscita: " + msg);
    }
  }

  function onLocalTest() {
    localTestNotification();
  }

  const disabledEnable =
    state.reason !== "ok" ||
    !state.swOk ||
    (state.perm === "granted" && state.hasSub) ||
    !HAS_VAPID;

  return (
    <div className="mt-6 rounded-lg border p-4 bg-white/60">
      <h3 className="font-semibold mb-2">Promemoria giornalieri</h3>
      <p className="text-sm text-gray-600 mb-3">
        Ricevi una notifica all’orario che preferisci per ricordarti esercizi e
        lezioni.
      </p>

      {/* ✅ BOX ROSSO + LINK BLU: solo in dashboard */}
      {showDashboardHint && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 mb-3">
          <div className="text-red-700 font-semibold mb-1">Attenzione</div>
          <div className="text-sm text-red-700">
            Dopo aver attivato le notifiche bisogna salvare. Anche se è arrivata
            la notifica di prova, se non salvi le altre notifiche non arriveranno.
            <br />
            Vai in Impostazioni, scegli l&apos;orario delle notifiche e fai
            “Salva impostazioni”.
          </div>
        </div>
      )}

      {!HAS_VAPID && (
        <p className="text-sm text-red-600 mb-3">
          Variabile <b>VITE_VAPID_PUBLIC_KEY</b> mancante in Cloudflare Pages.
        </p>
      )}

      {state.reason === "ios-need-install" && (
        <p className="text-sm text-orange-600 mb-3">
          Su iPhone installa l’app nella <b>Schermata Home</b> e riaprila.
        </p>
      )}

      {state.reason !== "ok" &&
        state.reason !== "checking" &&
        state.reason !== "ios-need-install" && (
          <p className="text-sm text-red-600 mb-3">
            Questo dispositivo o browser non supporta le Web Push ({state.reason}
            ).
          </p>
        )}

      {!state.swOk && state.reason === "ok" && (
        <p className="text-sm text-gray-600 mb-3">Inizializzazione in corso…</p>
      )}

      {state.lastErr && (
        <p className="text-sm text-red-600 mb-3">Errore: {state.lastErr}</p>
      )}

      <div className="flex gap-2">
        <Button onClick={onEnablePush} disabled={disabledEnable}>
          Attiva notifiche
        </Button>
        <Button variant="outline" onClick={onLocalTest}>
          Notifica di prova
        </Button>
      </div>

      {/* ✅ Link blu sotto (solo in dashboard) */}
      {showDashboardHint && (
        <div className="mt-2">
          <Link to="/impostazioni" className="text-blue-600 underline text-sm">
            Vai a Impostazioni
          </Link>
        </div>
      )}

      {/* ❌ rimosso: “Stato: permesso granted — subscription…” */}
    </div>
  );
}

export default PushSetupPanel;
